import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

import { devLog } from "@/lib/devLog";
/**
 * Plan flags: `{ can_add_branch, can_add_student, can_add_user, can_upload_file,
 * has_custom_branding, has_excel_import, has_multi_branch, ... }`
 *
 * Backed by GET /api/plan which is Owner/Admin only. For other roles the
 * context stays at its safe default: everything permitted (the backend gate
 * still catches limit violations).
 */
const PlanFlagsCtx = createContext({
  flags: null,
  plan: null,
  meta: null,
  loading: false,
  refresh: () => {},
});

const PERMISSIVE = {
  can_add_branch: true, can_add_student: true, can_add_user: true, can_upload_file: true,
  has_custom_branding: true, has_excel_import: true, has_multi_branch: true,
  has_api_access: false, has_priority_support: false,
  features: [],
};

export function PlanFlagsProvider({ children }) {
  const { user, activeRole } = useAuth();
  const [flags, setFlags] = useState(null);
  const [plan, setPlan] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    // Guardians/therapists never see this data — keep flags null so
    // downstream code falls back to the permissive default and stays out
    // of their way.
    if (!user || !["owner", "admin"].includes(activeRole)) {
      setFlags(null);
      setPlan(null);
      setMeta(null);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get("/plan");
      setFlags(data.flags || null);
      setPlan(data.plan || null);
      setMeta(data.meta || null);
    } catch (err) { devLog("plan flags fetch failed", err); }
    finally { setLoading(false); }
  }, [user, activeRole]);

  useEffect(() => { refresh(); }, [refresh]);

  const value = useMemo(
    () => ({ flags: flags || PERMISSIVE, plan, meta, loading, refresh, rawFlags: flags }),
    [flags, plan, meta, loading, refresh],
  );

  return (
    <PlanFlagsCtx.Provider value={value}>
      {children}
    </PlanFlagsCtx.Provider>
  );
}

export function usePlanFlags() {
  return useContext(PlanFlagsCtx);
}

/**
 * Reason strings we show inside "Upgrade" tooltips + banners. Keep concise —
 * they surface via title=, aria-label=, and small badges next to blocked
 * buttons.
 */
export const UPGRADE_REASON = {
  can_add_branch:      "Branch limit reached — upgrade in Settings → Plan & Usage.",
  can_add_student:     "Student limit reached — upgrade in Settings → Plan & Usage.",
  can_add_user:        "User limit reached — upgrade in Settings → Plan & Usage.",
  can_upload_file:     "Storage limit reached — free space or upgrade in Settings → Plan & Usage.",
  has_custom_branding: "Custom branding is not in your plan — upgrade in Settings → Plan & Usage.",
  has_excel_import:    "Excel import is not in your plan — upgrade in Settings → Plan & Usage.",
  has_multi_branch:    "Multi-branch requires a higher plan — upgrade in Settings → Plan & Usage.",
};
